// Form validation utilities for admin components
export const validateUserForm = (userData) => {
  const errors = {};
  
  // Required fields validation
  if (!userData.name || userData.name.trim().length < 2) {
    errors.name = 'Name must be at least 2 characters long';
  }
  
  if (!userData.email || !isValidEmail(userData.email)) {
    errors.email = 'Please enter a valid email address';
  }
  
  if (!userData.role || !['admin', 'agent', 'manager', 'end_user'].includes(userData.role)) {
    errors.role = 'Please select a valid role';
  }
  
  if (!userData.department || userData.department.trim().length < 2) {
    errors.department = 'Department must be at least 2 characters long';
  }
  
  // Phone validation (optional but if provided, must be valid)
  if (userData.phone && !isValidPhone(userData.phone)) {
    errors.phone = 'Please enter a valid phone number';
  }
  
  // Location validation (optional)
  if (userData.location && userData.location.trim().length < 2) {
    errors.location = 'Location must be at least 2 characters long';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

export const validateWorkflowForm = (workflowData) => {
  const errors = {};
  
  if (!workflowData.name || workflowData.name.trim().length < 3) {
    errors.name = 'Workflow name must be at least 3 characters long';
  }
  
  if (!workflowData.description || workflowData.description.trim().length < 10) {
    errors.description = 'Description must be at least 10 characters long';
  }
  
  if (!workflowData.category || !['ticket', 'user', 'system', 'notification'].includes(workflowData.category)) {
    errors.category = 'Please select a valid category';
  }
  
  if (!workflowData.conditions || !Array.isArray(workflowData.conditions) || workflowData.conditions.length === 0) {
    errors.conditions = 'At least one condition must be defined';
  }
  
  if (!workflowData.actions || !Array.isArray(workflowData.actions) || workflowData.actions.length === 0) {
    errors.actions = 'At least one action must be defined';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

export const validateSLAPolicyForm = (slaData) => {
  const errors = {};
  
  if (!slaData.name || slaData.name.trim().length < 3) {
    errors.name = 'SLA policy name must be at least 3 characters long';
  }
  
  if (!slaData.description || slaData.description.trim().length < 10) {
    errors.description = 'Description must be at least 10 characters long';
  }
  
  if (!slaData.priority || !['low', 'medium', 'high', 'critical'].includes(slaData.priority)) {
    errors.priority = 'Please select a valid priority';
  }
  
  if (!slaData.responseTime || slaData.responseTime < 1) {
    errors.responseTime = 'Response time must be at least 1 hour';
  }
  
  if (!slaData.resolutionTime || slaData.resolutionTime < slaData.responseTime) {
    errors.resolutionTime = 'Resolution time must be greater than response time';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Helper functions
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const isValidPhone = (phone) => {
  const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
  return phoneRegex.test(phone.replace(/[\s\-()]/g, ''));
};

// Input sanitization
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  return input
    .trim()
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, ''); // Remove event handlers
};

// Form field validation with real-time feedback
export const validateField = (fieldName, value, rules = {}) => {
  const errors = [];
  
  // Required field check
  if (rules.required && (!value || value.trim().length === 0)) {
    errors.push(`${fieldName} is required`);
    return errors;
  }
  
  // Skip other validations if field is empty and not required
  if (!value || value.trim().length === 0) {
    return errors;
  }
  
  // Min length check
  if (rules.minLength && value.length < rules.minLength) {
    errors.push(`${fieldName} must be at least ${rules.minLength} characters long`);
  }
  
  // Max length check
  if (rules.maxLength && value.length > rules.maxLength) {
    errors.push(`${fieldName} must be no more than ${rules.maxLength} characters long`);
  }
  
  // Pattern check
  if (rules.pattern && !rules.pattern.test(value)) {
    errors.push(`${fieldName} format is invalid`);
  }
  
  // Custom validation
  if (rules.custom && typeof rules.custom === 'function') {
    const customError = rules.custom(value);
    if (customError) {
      errors.push(customError);
    }
  }
  
  return errors;
};

// Common validation rules
export const validationRules = {
  email: {
    required: true,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    custom: (value) => {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        return 'Please enter a valid email address';
      }
      return null;
    }
  },
  phone: {
    pattern: /^[+]?[1-9][\d]{0,15}$/,
    custom: (value) => {
      const cleanPhone = value.replace(/[\s\-()]/g, '');
      if (!/^[+]?[1-9][\d]{0,15}$/.test(cleanPhone)) {
        return 'Please enter a valid phone number';
      }
      return null;
    }
  },
  name: {
    required: true,
    minLength: 2,
    maxLength: 100,
    custom: (value) => {
      if (!/^[a-zA-Z\s]+$/.test(value)) {
        return 'Name can only contain letters and spaces';
      }
      return null;
    }
  },
  password: {
    required: true,
    minLength: 8,
    custom: (value) => {
      if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) {
        return 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
      }
      return null;
    }
  }
};
